import styled, { StyledComponent } from "@emotion/styled";
import React, { AllHTMLAttributes, DetailedHTMLProps, forwardRef, HTMLAttributes, Ref, useMemo } from "react";

const VisuallyHiddenRoot = styled("span")`
    border: 0;
    clip: rect(0 0 0 0);
    clip-path: inset(50%); // TODO do we need this?
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;

    // https://medium.com/@jessebeach/beware-smushed-off-screen-accessible-text-5952a4c2cbfe
    white-space: nowrap;
    word-wrap: normal;
`;

export interface VisuallyHiddenProps<
    // We need to leave this generic in here to avoid breaking changes, but it's not actually used.
    // It wasn't truly constraining anything, and the union types it created was causing a lot of
    // slowdown in the typecript build.
    // Remove this in a major release.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    ElementType extends keyof JSX.IntrinsicElements = "span"
> extends AllHTMLAttributes<HTMLElement> {
    children: React.ReactNode;

    /**
     * A `clientId` which is a unique string that appears as a data attribute `data-client-id`
     * in the rendered code, serving as a hook for automated tests. Will be applied to the root
     * element of this component.
     */
    clientId?: string;

    /**
     * The type of HTML element to render as.
     *
     * Defaults to "span".
     */
    renderAs?: keyof JSX.IntrinsicElements;
}

/**
 * Adapted from https://github.com/reach/reach-ui/blob/main/packages/visually-hidden/src/index.tsx
 *
 * Provides text for screen readers that is visually hidden.
 * It is the logical opposite of the `aria-hidden` attribute.
 */
export const VisuallyHidden = forwardRef(
    ({ clientId, renderAs, ...rest }: VisuallyHiddenProps, ref: Ref<HTMLElement>): JSX.Element => {
        const TheComponent = useMemo(
            () => (renderAs ? VisuallyHiddenRoot.withComponent(renderAs) : VisuallyHiddenRoot),
            [renderAs]
        ) as StyledComponent<
            DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>,
            Record<string, unknown>,
            Record<string, unknown>
        >;
        return <TheComponent {...rest} data-client-id={clientId} ref={ref} />;
    }
);

VisuallyHidden.displayName = "VisuallyHidden";
